<template>
  <div
    class="grid grid-cols-1 gap-4 px-4 *:data-[slot=card]:to-card *:data-[slot=card]:shadow-xs lg:px-6 @xl/main:grid-cols-2 @5xl/main:grid-cols-4 dark:*:data-[slot=card]:bg-card"
  >
    <Card v-for="card in cards" :key="card.title" class="@container/card">
      <CardHeader>
        <CardDescription>{{ card.title }}</CardDescription>
        <CardTitle class="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          <Skeleton v-if="!loaded" class="h-8 w-24" />
          <div v-else class="min-w-0 truncate" :title="card.exact">
            {{ card.value }} <span class="text-muted-foreground font-medium text-xs italic leading-0">{{ card.footerNote }}</span>
          </div>
        </CardTitle>
        <CardAction>
          <component :is="card.icon" class="size-5 text-card-icon" />
        </CardAction>
      </CardHeader>
    </Card>
  </div>
</template>

<script>
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@boogi/ui'
import { Users, UserCheck, Activity, BadgePoundSterling } from 'lucide-vue-next'

export default {
  name: 'SectionCards',
  components: {
    Card,
    CardAction,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Skeleton,
  },
  data() {
    return {
      totalUsers: undefined,
      insights: undefined,
      Users,
      UserCheck,
      Activity,
      BadgePoundSterling,
    }
  },
  computed: {
    loaded() {
      return this.totalUsers !== undefined && this.insights !== undefined
    },
    cards() {
      const i = this.insights || {}
      const money = (v) =>
        '£' +
        Number(v || 0).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      const enrolled = this.totalUsers != null ? this.totalUsers : i.totalUsers
      return [
        {
          title: 'Total enrolled',
          value: Number(enrolled || 0).toLocaleString(),
          icon: Users,
          footerNote: 'All time',
        },
        {
          title: 'Monthly active users',
          value: Number(i.activeUsers || 0).toLocaleString(),
          icon: UserCheck,
          footerNote: 'MAUs',
        },
        {
          title: 'Transactions processed',
          value: Number(i.totalTransactionsProcessed || 0).toLocaleString(),
          icon: Activity,
          footerNote: 'All time',
        },
        {
          // Volume can be very large; show a compact form (e.g. £17M) so it never
          // overflows the card, with the exact amount available on hover.
          title: 'Payment volume generated',
          value: this.compactMoney(i.totalTransactionVolume),
          exact: money(i.totalTransactionVolume),
          icon: BadgePoundSterling,
          footerNote: 'All time',
        },
      ]
    },
  },
  created() {
    this.fetchSummary()
    this.fetchInsights()
  },
  methods: {
    // Compact money form (e.g. £17M) so large volumes never overflow the card.
    compactMoney(v) {
      return new Intl.NumberFormat('en-GB', {
        notation: 'compact',
        style: 'currency',
        currency: 'GBP',
        maximumFractionDigits: 1,
      }).format(Number(v || 0))
    },
    authHeaders() {
      return {
        'Content-Type': 'application/json',
        Authorization: `${localStorage.getItem('token')}`,
      }
    },
    apiBase() {
      return localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
    },
    async fetchSummary() {
      try {
        const res = await this.$apiFetch(`${this.apiBase()}/private/api/client/audience/top-bar`, {
          method: 'GET',
          headers: this.authHeaders(),
        })
        this.totalUsers = res.ok ? (await res.json()).totalUsers : 0
      } catch (e) {
        this.totalUsers = 0
      }
    },
    async fetchInsights() {
      try {
        const res = await this.$apiFetch(`${this.apiBase()}/private/api/client/analytics/homepage`, {
          method: 'GET',
          headers: this.authHeaders(),
        })
        this.insights = res.ok ? await res.json() : {}
      } catch (e) {
        this.insights = {}
      }
    },
  },
}
</script>
